import { useEffect, useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { fetchSelector, postRequestGetOtp, resendFinancialOtp } from "@/api/user/home";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import CustomButton from "@/shared/components/kit/button";
import { CircularProgress } from "@mui/material";
import OtpTab from "@/modules/user/components/credits/validation/payment/validationAfterPayment/components/otpTab";

interface FinancialOtpStepProps {
    phone: string;
    onSuccess: () => void;
}
interface OtpStatus {
    otp: boolean;
    error: boolean;
    success: boolean;
    reload: boolean;
}

type ErrorResponse = {
    data: {
        message: string;
        status: string;
    };
};


const FinancialOtpStep = ({ phone, onSuccess }: FinancialOtpStepProps) => {
    const [valueOtp, setValueOtp] = useState("");
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const [otpRequestData, setOtpRequestData] = useState<{ message: string; status: string; time: number } | null>(null);
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [isWaitingForOtp, setIsWaitingForOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const hasSentOtp = useRef(false);
    const [otpStatus, setOtpStatus] = useState<OtpStatus>({
        otp: false,
        error: false,
        success: false,
        reload: false
    });

    const { mutate: sendOtp, isPending: isSendingOtp } = useMutation({
        mutationFn: fetchSelector,
        onSuccess: (response: any) => {
        setOtpSent(true);
            setOtpStatus({
                otp: true,
                error: false,
                success: true,
                reload: false
            });
            toast.success(response.message || "کد تایید برای شما ارسال شد");
            if (response?.data) {
                setOtpRequestData(response.data);
                setTimeLeft(response.data.time || 60);
                setIsWaitingForOtp(true);
            }
        },
        onError: (error: AxiosError<{ message: string }>) => {
            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false
            });
            toast.error(error?.response?.data?.message || "خطا در دریافت اطلاعات");
        },
    });

    const { mutate: verifyOtp, isPending: isVerifying } = useMutation({
        mutationFn: postRequestGetOtp,
        onMutate: () => {
            setIsWaitingForOtp(true);
            setOtpRequestData({
                message: "درحال بارگذاری",
                status: "pending",
                time: 60
            });
            setTimeLeft(60);
        },
        onSuccess: (data) => {
            if (data?.data?.status === 'success') {
                setOtpStatus({
                    otp: true,
                    error: false,
                    success: true,
                    reload: false
                });
                toast.success(data?.data?.message || "تایید کد با موفقیت انجام شد");
                setTimeout(() => {
                    window.location.reload();
                }, 500);
                return;
            }

            if (data?.data?.status === 'error') {
                setOtpStatus({
                    otp: false,
                    error: true,
                    success: false,
                    reload: false
                });
                setOtpRequestData(data.data);
                setTimeLeft(data.data.time || 60);
                setIsWaitingForOtp(true);
                toast.error(data?.data?.message || "خطا در تایید کد");
                return;
            }

            if (data?.data?.status === 'expired') {
                setOtpStatus({
                    otp: false,
                    error: false,
                    success: false,
                    reload: true
                });
                toast.success(data?.data?.message || "کد مجددا برای شما ارسال شد");
                setTimeout(() => {
                    resendOtp();
                }, 500);
                return;
            }


            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false
            });
            toast.error(data?.data?.message || "خطا در تایید کد");
        },
        onError: (error: AxiosError<ErrorResponse>) => {
            if (error.response?.data?.data.status === 'incorrect') {

                setOtpStatus({
                    otp: true,
                    error: false,
                    success: true,
                    reload: false,
                });

                toast.error(
                    error.response?.data?.data.message || "لطفا مجدد کد را وارد کنید"
                );
                return;
            }

            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false,
            });

            setIsWaitingForOtp(false);
            setOtpRequestData(null);
            setTimeLeft(0);

            toast.error(
                error.response?.data?.data.message || "خطا در تایید کد"
            );
        },
    });

    const { mutate: resendOtp, isPending: isResending } = useMutation({
        mutationFn: resendFinancialOtp,
        onSuccess: (response: any) => {
            setIsResendingOtp(false);
            setOtpStatus({
                otp: true,
                error: false,
                success: true,
                reload: false
            });
            toast.success(response.message || "کد تایید مجدداً برای شما ارسال شد");
            if (response?.data) {
                setOtpRequestData(response.data);
                setTimeLeft(response.data.time || 60);
                setIsWaitingForOtp(true);
            }
        },
        onError: (error: AxiosError<{ message: string }>) => {
            setIsResendingOtp(false);
            setOtpStatus({
                otp: false,
                error: true,
                success: false,
                reload: false
            });
            toast.error(error?.response?.data?.message || "خطا در ارسال مجدد کد");
        },
    });

    useEffect(() => {
        if (!hasSentOtp.current) {
            hasSentOtp.current = true;
            console.log("🔵 FINANCIAL OTP STEP RENDERED - SENDING OTP ONCE!");
            sendOtp();
        }
    }, []);

    const handleContinue = () => {
        verifyOtp({ otp: valueOtp });
    };

    const handleResendOtp = () => {
        setValueOtp('');
        setIsResendingOtp(true);
        resendOtp();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" && isOtpValid && !isVerifying) {
            event.preventDefault();
            handleContinue();
        }
    };

    const isOtpValid = valueOtp && valueOtp.length >= 5;
    const shouldShowOtp = otpStatus.otp || otpStatus.success || (!otpStatus.error && !otpStatus.reload);

    // Check if we're in the initial loading state
    const isInitialLoading = isSendingOtp && !otpSent;

    return (
        <StackKit
            direction="column"
            sx={{
                width: "100%",
                height: "100%",
                alignItems: shouldShowOtp ? "flex-start" : "center",
                justifyContent: shouldShowOtp ? "space-between" : "center",
                padding: '1rem'
            }}
            onKeyDown={handleKeyDown}
        >
            {shouldShowOtp && !otpStatus.error ? (
                <>
                    {isInitialLoading ? (
                        <StackKit sx={{width: "100%", height: "100%"}} direction={"row"} justifyContent={"center"} alignItems={"center"}>
                            <CircularProgress sx={{color: "#fff"}} size={50}/>
                        </StackKit>
                    ) : (
                        <>
                            <OtpTab
                                dataPhone={phone}
                                valueOtp={valueOtp}
                                setValueOtp={setValueOtp}
                                handleSubmit={handleContinue}
                                onResendOtp={handleResendOtp}
                                isResendingOtp={isResendingOtp || isResending}
                                otpMessage={otpStatus.success ? "کد تایید شد" : otpRequestData?.message}
                                otpStatus={otpStatus.success ? "success" : otpRequestData?.status}
                                isWaitingForOtp={isVerifying}
                            />

                            {!isInitialLoading && (
                                <StackKit direction="row" justifyContent="end" width="100%">
                                    <CustomButton type="submit" onClick={handleContinue} variant="contained"
                                                  disabled={!isOtpValid || isVerifying} sx={{mt: 3, width: "fit-content"}}>
                                        <TypoGraphyKit variant="body1" sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            minWidth: "50px"
                                        }}>
                                            {isVerifying ? (
                                                <CircularProgress sx={{color: "#fff"}} size={22}/>
                                            ) : (
                                                "تایید کد"
                                            )}
                                        </TypoGraphyKit>
                                    </CustomButton>
                                </StackKit>
                            )}
                        </>
                    )}
                </>
            ) : (
                <StackKit direction="row" justifyContent="end" width="100%">
                    <StackKit sx={{width: "100%", height: "100%"}} direction={"row"} justifyContent={"center"} alignItems={"center"}>
                        <StackKit direction={"column"} sx={{width: "80%"}} alignItems={"center"}>
                            <TypoGraphyKit variant="h6" sx={{minHeight: "200px"}}>
                                {otpStatus.reload ? "در حال بارگذاری ..." : "خطا در ارتباط با سرویس اعتبار سنجی"}
                            </TypoGraphyKit>
                            {otpStatus.error && (
                                <CustomButton onClick={() => sendOtp()} variant="contained" sx={{mt: 2}}>
                                    تلاش مجدد
                                </CustomButton>
                            )}
                        </StackKit>
                    </StackKit>
                </StackKit>
            )}
        </StackKit>
    );
};

export default FinancialOtpStep;